import { plentyOneApi } from '@/services/plentyOneApi';
import { logger } from '@/services/logger';
import type { PricingType, PaymentMethodType } from '@/types/b2b';

// アカウント情報の型定義
export interface AccountInfo {
  erpContactId: number;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  classId?: number;
  className?: string;
  addresses: Address[];
  pricingType?: PricingType;
  paymentMethodType?: PaymentMethodType;
  vatId?: string;
}

export interface Address {
  id: number;
  name1?: string;
  name2?: string;
  address1: string;
  address2?: string;
  postalCode: string;
  town: string;
  countryId: number;
  contactRelations?: ContactRelation[];
  taxIdNumber?: string;
  options?: Array<{ typeId: number; value: string }>;
}

export interface ContactRelation {
  typeId: number;
  referrerId?: number;
  isPrimary?: boolean;
}

export interface RawContactInfo {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  classId?: number;
  pricing_type?: PricingType;
  payment_method_type?: PaymentMethodType;
  accounts?: Array<{
    id: number;
    companyName?: string;
    taxIdNumber?: string;
  }>;
}

export interface RawContactData {
  contactInfo: RawContactInfo;
  addresses?: Address[];
}

/**
 * アカウント情報を取得・整形する統合API関数
 * TanStack Queryの queryFn で使用される
 */
export const fetchAccountInfo = async (erpContactId: number): Promise<AccountInfo> => {
  if (!erpContactId) {
    throw new Error('Knitido Customer ID is required');
  }

  try {
    // 1. 基本的な連絡先情報を取得
    logger.debug('Fetching contact info');
    const contactData: RawContactData = await plentyOneApi.getContactInfo(erpContactId);
    
    if (!contactData?.contactInfo) {
      throw new Error('Contact information not found');
    }

    const { contactInfo, addresses = [] } = contactData;

    // 2. 顧客クラス名を取得（classIdが存在する場合のみ）
    let className: string | undefined;
    if (contactInfo.classId) {
      try {
        logger.debug('Fetching contact class');
        const classInfo = await plentyOneApi.getContactClass(contactInfo.classId);
        
        // APIレスポンスの構造に応じて名前を抽出
        if (typeof classInfo === 'string') {
          className = classInfo;
        } else if (classInfo && typeof classInfo === 'object' && 'name' in classInfo) {
          className = (classInfo as { name: string }).name;
        }
      } catch (error) {
        logger.warn('Failed to fetch contact class, continuing without it', { error: error instanceof Error ? error.message : String(error) });
        // クラス取得エラーは無視して続行
      }
    }

    // 3. 会社名の補完処理（優先順位: accounts.companyName > contactInfo.company > 請求先住所.name1）
    let companyName: string | undefined;

    // 優先度1: contactInfo.accounts から会社名を取得（ERPの正式な企業情報）
    if (contactInfo.accounts && contactInfo.accounts.length > 0 && contactInfo.accounts[0].companyName) {
      companyName = contactInfo.accounts[0].companyName;
      logger.debug(`Company name found in accounts: ${companyName}`);
    }

    // 優先度2: contactInfo.company を使用
    if (!companyName && contactInfo.company) {
      companyName = contactInfo.company;
      logger.debug(`Company name found in contactInfo: ${companyName}`);
    }

    // 優先度3: 請求先住所（typeId: 1, isPrimary優先）から会社名を取得
    if (!companyName && addresses.length > 0) {
      const billingAddresses = addresses.filter((addr: Address) =>
        addr.contactRelations?.some((rel: ContactRelation) => rel.typeId === 1)
      );
      const billingAddress = billingAddresses.find((addr: Address) =>
        addr.contactRelations?.some((rel: ContactRelation) => rel.typeId === 1 && rel.isPrimary === true)
      ) || billingAddresses[0];

      if (billingAddress?.name1) {
        companyName = billingAddress.name1;
        logger.debug(`Company name found in billing address: ${companyName}`);
      } else if (addresses[0]?.name1) {
        // フォールバック: 最初の住所の name1 を使用
        companyName = addresses[0].name1;
        logger.debug(`Company name fallback to first address: ${companyName}`);
      }
    }

    // 4. VAT IDを取得（堅牢なフォールバック処理）
    let vatId: string | undefined;
    
    // 優先度1: contactInfo.accounts 配列から取得
    if (contactInfo.accounts && contactInfo.accounts.length > 0 && contactInfo.accounts[0].taxIdNumber) {
      vatId = contactInfo.accounts[0].taxIdNumber;
      logger.debug(`VAT ID found in contact accounts: ${vatId}`);
    }

    // 優先度2: addresses 配列を検索
    if (!vatId && addresses && addresses.length > 0) {
      for (const address of addresses) {
        // 2a: address の taxIdNumber フィールド
        if (address.taxIdNumber) {
          vatId = address.taxIdNumber;
          logger.debug(`VAT ID found in address.taxIdNumber: ${vatId}`);
          break; // 最初に見つかったものを採用
        }
        // 2b: address の options 配列
        if (address.options && Array.isArray(address.options)) {
          const vatOption = address.options.find((opt: { typeId: number; value: string }) => opt.typeId === 1 && opt.value);
          if (vatOption) {
            vatId = vatOption.value;
            logger.debug(`VAT ID found in address.options: ${vatId}`);
            break; // 最初に見つかったものを採用
          }
        }
      }
    }

    // 5. 最終的なデータを整形
    const result: AccountInfo = {
      erpContactId: contactInfo.id,
      firstName: contactInfo.firstName,
      lastName: contactInfo.lastName,
      email: contactInfo.email,
      company: companyName || contactInfo.company || '',
      classId: contactInfo.classId,
      className,
      addresses,
      pricingType: contactInfo.pricing_type,
      paymentMethodType: contactInfo.payment_method_type,
      vatId,
    };

    logger.info('Account info successfully fetched and processed');
    return result;

  } catch (error) {
    logger.error('Error in fetchAccountInfo', { error: error instanceof Error ? error.message : String(error) });
    throw error; // TanStack Queryがエラー状態を管理するために再スロー
  }
};

/**
 * アカウント情報を無効化するためのヘルパー関数
 * プロフィール更新後などに使用
 */
export const getAccountInfoQueryKey = (erpContactId: number | null | undefined) => {
  return ['accountInfo', erpContactId] as const;
};